/**
 * MisreadReportSheet — SC-027 스캔 오인식 신고 (FN-074).
 *
 * 진입점이 셋이라 **화면이 아니라 부품**으로 만들었다:
 *   SC-010 인식 결과 · SC-011 요약 · SC-002 보유 카드 — 전부 **카드 행 단위**다.
 *   화면 단위로 만들면 "뭔가 틀렸다"만 오고 어느 카드인지 특정이 안 된다.
 *
 * ── 두 단 ───────────────────────────────────────────────────────────
 *   form  유형 고르기 → (선택) 정답 카드 → 보내기
 *   done  접수 확인 → (선택) 보유에서 빼기 → 닫기
 *
 * ⚠ **접수해도 보유에서 자동으로 빼지 않는다.** 완료 화면에서 빼는 길만 열어 준다.
 *   자동으로 빼면 신고가 삭제 버튼처럼 쓰여, 들어오는 데이터가 통째로 오염된다
 *   ('틀려서'가 아니라 '지우려고' 누른 신고가 섞인다).
 * ⚠ **정답 카드는 선택이다.** 필수로 하면 "뭔지 모르겠는데 틀린 건 안다"는
 *   가장 흔한 신고가 막힌다.
 * ⚠ **비회원은 신고할 수 없다.** 누른 순간 로그인으로 보낸다 — 다 적고 나서
 *   막으면 적은 것이 사라진다. (이 판단은 여는 쪽에서 한다: useMisreadReport)
 * ⚠ 보상도, 개별 답변도 없다. 화면정의서는 그 고지를 시트 안에 적으라고 했지만
 *   **걷었다** (2026-08-27). 보내기 버튼 바로 위에서 "답은 안 드려요"를 읽히면,
 *   마지막으로 남는 말이 거절이 된다 — 알려 주려던 사람의 손이 거기서 멈춘다.
 *   접수 완료 화면의 '고맙습니다'가 같은 사실을 더 나은 자리에서 말한다.
 */
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "@astryxdesign/core/Button";
import { Divider } from "@astryxdesign/core/Divider";
import { Heading } from "@astryxdesign/core/Heading";
import { RadioList, RadioListItem } from "@astryxdesign/core/RadioList";
import { Text } from "@astryxdesign/core/Text";
import { TextInput } from "@astryxdesign/core/TextInput";
import { VStack, HStack } from "@astryxdesign/core/Layout";
import { List } from "@astryxdesign/core/List";
import { Item } from "@astryxdesign/core/Item";
import Sheet from "../feedback/Sheet";
import CardThumb from "../card/CardThumb";
import {
  REPORT_KINDS,
  asksForCorrection,
  canSubmit,
  submitMisreadReport,
  type ReportKind,
} from "./misreadReport";
import { cardImageUrl, searchCards, type CardItem } from "../../api/card";
import { useScanLangStore } from "../../store/scanLangStore";
import errorFront from "../../assets/pictograms/peeka-error-front.png";
import styles from "./MisreadReportSheet.module.css";

/** 검색 결과는 다섯 줄까지. 시트 안이라 더 길면 보내기 버튼이 밀려난다 */
const MAX_SUGGESTIONS = 5;
/** 검색어를 멈춘 뒤 질의까지 — 글자마다 쏘면 시트 하나가 카탈로그를 두들긴다 */
const SEARCH_DEBOUNCE_MS = 300;

/**
 * 신고 대상·정답 카드의 최소 모양.
 *
 * ⚠ 원본(design)은 `cardUid` 만 받아 더미 카탈로그에서 찾았다. dev 는 실 카탈로그라
 *   그 조회가 성립하지 않는다 — **부르는 쪽이 이미 들고 있는 카드**를 그대로 넘긴다.
 *   보유 목록(HoldingCard)·검색 결과(CardItem) 가 둘 다 이 모양을 만족한다.
 */
export interface ReportCard {
  cardUid: string;
  /** 카탈로그 일련번호. 인덱스⊃카탈로그라 **없을 수 있다** */
  cardSerialNo?: number;
  name: string;
  number: string | null;
  cardsetCode: string;
  imagePathway: string | null;
}

/** 티커 — 세트코드-번호. 번호 없는 인쇄판(일본판 기본 에너지)이 실재해 세트코드만 남는다 */
function ticker(card: ReportCard): string {
  const code = card.cardsetCode.toUpperCase();
  return card.number ? `${code}-${card.number}` : code;
}

export interface MisreadReportSheetProps {
  open: boolean;
  onClose: () => void;
  /** 신고 대상 카드 */
  card: ReportCard;
  /** 스캔한 시각 (ISO) — 어느 시세·모델 판이었는지 되짚는 데 쓴다 */
  scannedAt?: string;
  /**
   * 보유에서 빼는 길. **완료 단에서만** 쓴다.
   * 넘기지 않으면 그 버튼이 안 나온다 — 보유가 아닌 자리(SC-010 인식 직후)에서
   * '보유에서 빼기'를 내밀면 무엇을 빼는지가 안 맞는다.
   */
  onRemoveHolding?: () => void;
}

export default function MisreadReportSheet({
  open,
  onClose,
  card,
  scannedAt,
  onRemoveHolding,
}: MisreadReportSheetProps) {
  const { t } = useTranslation("card");
  const scanLang = useScanLangStore((s) => s.lang);

  const [kind, setKind] = useState<ReportKind | null>(null);
  const [correct, setCorrect] = useState<ReportCard | null>(null);
  const [query, setQuery] = useState("");
  const [suggestions, setSuggestions] = useState<CardItem[]>([]);
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(false);

  /* 닫혔다 다시 열리면 **빈 종이로 시작한다.** 남아 있으면 다른 카드를 신고하는데
     앞 카드에 골랐던 유형이 이미 찍혀 있다 — 그대로 보내지기 쉽다. */
  useEffect(() => {
    if (open) return;
    setKind(null);
    setCorrect(null);
    setQuery("");
    setSuggestions([]);
    setSending(false);
    setDone(false);
  }, [open]);

  /* 정답 카드 검색 — 실 카탈로그 질의다.
     ⚠ 스캔 언어로 좁힌다. 신고하는 사람이 손에 든 것은 그 언어판이고, 안 좁히면
       같은 그림의 세 언어판이 다섯 줄을 다 채워 정작 고를 것이 안 보인다.
     ⚠ 신고 대상 자신은 뺀다 — '이 카드가 아니라 이 카드' 라고 말하는 자리다. */
  useEffect(() => {
    if (!asksForCorrection(kind) || correct || query.trim().length === 0) {
      setSuggestions([]);
      return;
    }
    let cancelled = false;
    const id = window.setTimeout(() => {
      void searchCards({ keyword: query.trim(), language: scanLang, size: MAX_SUGGESTIONS + 1 })
        .then((res) => {
          if (cancelled) return;
          setSuggestions(
            res.items.filter((c) => c.cardUid !== card.cardUid).slice(0, MAX_SUGGESTIONS),
          );
        })
        /* 검색이 안 되는 것은 신고를 막을 이유가 아니다 — 정답 카드는 선택이다 */
        .catch(() => {
          if (!cancelled) setSuggestions([]);
        });
    }, SEARCH_DEBOUNCE_MS);
    return () => {
      cancelled = true;
      window.clearTimeout(id);
    };
  }, [kind, correct, query, scanLang, card.cardUid]);

  const handleSend = async () => {
    if (!canSubmit(kind) || sending) return;
    setSending(true);
    await submitMisreadReport({
      cardUid: card.cardUid,
      cardSerialNo: card.cardSerialNo,
      kind: kind as ReportKind,
      correctCardUid: correct?.cardUid,
      correctCardSerialNo: correct?.cardSerialNo,
      scannedAt,
    });
    setSending(false);
    setDone(true);
  };

  return (
    <Sheet open={open} onClose={onClose} ariaLabel={t("report.title")}>
      <VStack gap={4} padding={5}>
        {done ? (
          /* ── 접수 완료 ─────────────────────────────────────────── */
          <VStack gap={4}>
            {/* 신고 픽토그램 — 접수 화면의 표식 (2026-08-28).
                글자 두 줄만 있던 자리라 '보냈다' 가 담담하게 지나갔다. 그림이
                서면 이 창이 **무엇에 대한 끝인지**가 한눈에 남는다.
                ⚠ error 계열을 쓴다. 방금 알려 준 것이 '잘못된 것' 이라서다 —
                  고맙다는 말과 함께 서 있어도 어긋나지 않는다. 표식은 사용자의
                  기분이 아니라 **다룬 대상**을 가리킨다.
                ⚠ 장식이다. 뜻은 아래 두 줄이 다 말하므로 alt 를 비운다. */}
            <img className={styles.doneMark} src={errorFront} alt="" aria-hidden="true" draggable={false} />
            <VStack gap={1}>
              <Heading level={3}>{t("report.doneTitle")}</Heading>
              <Text type="supporting" color="secondary">{t("report.doneBody")}</Text>
            </VStack>

            {/* 보유에서 빼기 — **여기서만** 내민다. 자동이 아니라 한 번 더 누르는
                자리다. 신고와 삭제는 다른 일이고, 둘 다 하고 싶은 사람이 많을 뿐이다. */}
            {onRemoveHolding && (
              <>
                <Divider />
                <VStack gap={2} align="start">
                  <Text type="supporting" color="secondary">{t("report.removeHint")}</Text>
                  <Button
                    variant="secondary"
                    width="100%"
                    label={t("report.removeAction")}
                    clickAction={() => {
                      onRemoveHolding();
                      onClose();
                    }}
                  />
                </VStack>
              </>
            )}
          </VStack>
        ) : (
          /* ── 신고 작성 ─────────────────────────────────────────── */
          <>
            <Heading level={3}>{t("report.title")}</Heading>

            {/* 무엇을 신고하는지 — 행 단위 신고라 대상이 늘 보여야 한다 */}
            <HStack gap={3} align="center" className={styles.target}>
              <CardThumb imgUrl={cardImageUrl(card.imagePathway) ?? undefined} />
              <VStack gap={0.5} align="start">
                <Text weight="semibold" maxLines={1}>{card.name}</Text>
                <Text type="supporting" color="secondary">{ticker(card)}</Text>
              </VStack>
            </HStack>

            <RadioList
              label={t("report.kindLabel")}
              value={kind ?? ""}
              onChange={(v) => {
                setKind(v as ReportKind);
                /* 유형을 바꾸면 앞 유형에서 짚어 둔 정답 카드는 뜻을 잃는다 */
                setCorrect(null);
                setQuery("");
              }}
            >
              {REPORT_KINDS.map((k) => (
                <RadioListItem
                  key={k}
                  value={k}
                  label={t(`report.kind.${k}`)}
                  description={t(`report.kindDesc.${k}`)}
                />
              ))}
            </RadioList>

            {/* 정답 카드 — '다른 카드예요' 일 때만. 나머지 셋은 카드가 맞다는 전제라
                물어볼 것이 없다. */}
            {asksForCorrection(kind) && (
              <VStack gap={2} align="start">
                <Text type="supporting" color="secondary">{t("report.correctHint")}</Text>
                {correct ? (
                  <HStack gap={2} align="center" justify="between" width="100%">
                    <HStack gap={2} align="center">
                      <CardThumb imgUrl={cardImageUrl(correct.imagePathway) ?? undefined} />
                      <VStack gap={0.5} align="start">
                        <Text weight="medium" maxLines={1}>{correct.name}</Text>
                        <Text type="supporting" color="secondary">{ticker(correct)}</Text>
                      </VStack>
                    </HStack>
                    <Button
                      variant="ghost"
                      label={t("report.correctClear")}
                      clickAction={() => setCorrect(null)}
                    />
                  </HStack>
                ) : (
                  <>
                    <TextInput
                      label={t("report.correctSearch")}
                      isLabelHidden
                      placeholder={t("report.correctPlaceholder")}
                      value={query}
                      onChange={setQuery}
                    />
                    {suggestions.length > 0 && (
                      <List hasDividers>
                        {suggestions.map((c) => (
                          <Item
                            key={c.cardUid}
                            as="li"
                            label={c.name}
                            description={ticker(c)}
                            labelLines={1}
                            startContent={<CardThumb imgUrl={cardImageUrl(c.imagePathway) ?? undefined} />}
                            onClick={() => setCorrect(c)}
                          />
                        ))}
                      </List>
                    )}
                    {/* 건너뛰기가 **눈에 보여야** 한다. 검색창만 있으면 골라야만
                        보낼 수 있는 줄 알고 여기서 멈춘다. */}
                    <Text type="supporting" color="secondary">{t("report.correctSkip")}</Text>
                  </>
                )}
              </VStack>
            )}

            <Divider />

            {/* 사진 고지 — **사진은 가지 않는다**고 말하는 줄이다 (2026-09-15).
                전에는 정반대를 말했다. 체크박스(기본 켬·끌 수 있음) → 끌 수 없는
                고지("스캔한 사진이 함께 전송돼요") 로 옮겨 오는 동안, 정작 **보내는
                코드가 한 번도 없었다** — submitMisreadReport 는 JSON 메타데이터만
                보내고 BE 는 PHOTO_ATCH_YN 플래그만 적는다.
                말이 동작보다 앞서 나간 것인데, 그 방향의 어긋남은 개인정보 고지에서
                제일 위험하다 — 하지도 않는 수집을 약속한 꼴이다.
                ⚠ 그래서 지금은 Play 데이터 보안 양식의 '사진 및 동영상' 이 **수집 안 함**
                  으로 맞다. 일반 빌드에서 사진이 서버로 가는 길이 없다 — 라벨 수집
                  (/scan/labels)은 디버그 메뉴로 켜는 라벨링 모드 전용이다.
                ⚠ 사진을 실제로 붙이는 날 고칠 것은 이 줄만이 아니다. Play 양식과
                  개인정보처리방침(수집 목적·보관 기간·삭제 시점)이 **먼저** 서 있어야
                  하고, 첨부가 되는 자리도 스캔 요약 하나뿐이다 — 보유 카드에서 여는
                  신고는 그 시점에 촬영 원본이 이미 없다. */}
            <Text type="supporting" color="secondary">
              {t("report.photoNotice")}
            </Text>

            <Button
              variant="primary"
              size="lg"
              width="100%"
              label={t("report.send")}
              isDisabled={!canSubmit(kind)}
              isLoading={sending}
              clickAction={handleSend}
            />
          </>
        )}
      </VStack>
    </Sheet>
  );
}
